/**
 * Microsoft Agent 365: every enterprise team AIOE governs gets an Entra agent identity of its own,
 * a card in the Agent 365 registry, and its runs exported to Agent 365 observability, so the
 * organisation's Microsoft 365 administrators see AIOE's agents beside everyone else's
 * (docs/research/2026-10-microsoft-work-iq.md, option B). Enterprise edition only.
 *
 *   GET    /admin/v1/agent365                    Settings → Microsoft Agent 365: the connection, every team and where it stands
 *   PUT    /admin/v1/agent365                    { blueprintAppId, clientSecret }: connect the agent identity blueprint
 *   DELETE /admin/v1/agent365                    forget the blueprint (identities already created stay in Entra)
 *   POST   /admin/v1/agent365/teams              give every team without one an agent identity
 *   POST   /admin/v1/agent365/retry              try every team's registration once more; { retried, ok, failed }
 *   POST   /admin/v1/agent365/teams/:id          give one team an agent identity, or try again after a failure
 *   DELETE /admin/v1/agent365/teams/:id          delete the team's agent identity and its registry card
 *   POST   /admin/v1/agent365/reconcile          list every identity under the blueprint and name the ones that belong to no current member
 *   POST   /admin/v1/agent365/orphans/disable    { ids }: turn those identities off in Entra (accountEnabled false)
 *   POST   /admin/v1/agent365/orphans/delete     { ids }: delete those, only when disabled and listed by the last reconcile
 *
 * An identity carries a stable key as a tag (`aioe:<tenantSlug>:team:<teamId>:member:<memberName>`),
 * so a member is never minted twice: AIOE's record first, then an identity under the blueprint
 * carrying the key (or, for one made before the key, its exact display name), and only then a new one.
 * The blueprint is created once by an Entra administrator (Entra ID → Agents → Agent blueprints,
 * or Microsoft Graph); AIOE holds its id and either a client secret, sealed, or uses the
 * deployment's managed identity as the blueprint's federated credential, and creates agent
 * identities from it. Tokens for export follow the agent autonomous app flow: the blueprint impersonates the
 * agent identity (`fmi_path`), then the identity gets an app-only token for the observability
 * service. Nothing a person signs in with is ever used.
 */
import { z } from "zod";
/** The Agent 365 observability service's app id: the audience of every export token. */
export const AGENT365_OBSERVABILITY_APP_ID = "9b975845-388f-4429-889e-eab1ef63949c";
const guid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "an Entra application (client) id, a GUID");
/** How AIOE proves it is the blueprint: a client secret it keeps sealed, or the deployment's managed identity, which the blueprint trusts as a federated credential. */
export const AGENT365_CREDENTIALS = ["secret", "managed-identity"];
export const agent365CredentialSchema = z.enum(AGENT365_CREDENTIALS);
export const agent365ConnectSchema = z
    .object({
    blueprintAppId: guid,
    credential: agent365CredentialSchema.default("secret"),
    /** With `credential: secret`. */
    clientSecret: z.string().min(1).max(4000).optional(),
    /** The person accountable for the agents, by Entra object id; the connecting administrator when left out. */
    sponsorId: z.string().min(1).max(200).optional(),
})
    .refine((b) => b.credential !== "secret" || Boolean(b.clientSecret), { message: "a client secret, or the managed identity", path: ["clientSecret"] });
export const agent365OrphanIdsSchema = z.object({
    ids: z.array(z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "an agent identity's object id")).min(1).max(200),
});
export const agent365OrderCreateSchema = z.object({
    action: z.enum(["disable", "quarantine", "enable"]),
    /** A specialist's id, or `<teamId>/<memberName>` for a team member. */
    specialistId: z.string().min(1).max(200),
    entraAgentId: z.string().min(1).max(100).optional(),
    reason: z.string().max(500).optional(),
});
/**
 * The customer's Microsoft Sentinel workspace (the workbench contract, C1-§4): audit rows worth a
 * detection go to a data collection rule the platform writes to as the blueprint. The customer
 * deploys the shipped table and rule (the workbench repository's docs/integrations/microsoft-sentinel)
 * and gives the blueprint's principal the Monitoring Metrics Publisher role on the rule.
 *
 *   GET    /admin/v1/sentinel                 Settings → Microsoft Sentinel
 *   PUT    /admin/v1/sentinel                 { endpoint, ruleId, stream? }: connect (proven with an empty batch)
 *   DELETE /admin/v1/sentinel                 forget it
 *   GET    /admin/v1/audit/export?format=sentinel   the same rows, for a backfill
 */
export const SENTINEL_STREAM = "Custom-MojoUpSecurityEvents";
export const sentinelConnectSchema = z.object({
    /** The data collection endpoint's logs ingestion address, https://<dce>.<region>-1.ingest.monitor.azure.com */
    endpoint: z.string().url().max(300),
    /** The rule's immutable id (dcr-…). */
    ruleId: z.string().min(1).max(200),
    stream: z.string().min(1).max(200).default(SENTINEL_STREAM),
});
/** A row's origin as Sentinel columns (SentinelRow's Client* fields). */
export function sentinelOrigin(o) {
    if (!o)
        return {};
    const l = o.location;
    return {
        ...(o.ip ? { ClientIp: o.ip } : {}),
        ...(l?.country ? { ClientCountry: l.country } : {}),
        ...(l?.region ? { ClientRegion: l.region } : {}),
        ...(l?.city ? { ClientCity: l.city } : {}),
        ...(o.userAgent ? { UserAgent: o.userAgent } : {}),
        ...(o.client ? { ClientApp: o.client.summary, ClientKind: o.client.kind, ...(o.client.version ? { ClientVersion: o.client.version } : {}) } : {}),
    };
}
//# sourceMappingURL=agent365.js.map